import { action, observable } from "mobx";
import { persist } from "mobx-persist";
import {
  MainDatabaseRef,
  PublicDataDatabaseRef,
  UkaiDatabaseRef,
  firebase,
  firebaseApp,
} from "../config/firebase";
import SyncProgramStore from "./SyncProgramStore";
import SessionStore from "./sessionStore";
import { generateUUID } from "src/lib/uuid";

import { isProduction } from 'src/utils/Constants'
import { isInIframe } from "src/utils/authLinked";

export function functionUrl(path) {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (isProduction) {
    return `https://us-central1-obat-io.cloudfunctions.net${normalized}`;
  }
  return `http://localhost:5001/obat-io/us-central1${normalized}`;
}

const BROWSER_SESSION_STORAGE_KEY = "obat_browser_session_id";

class AppState {
  @persist @observable authenticated = false;
  @observable authenticating = true;
  _authUnsub = null;

  @persist("object") @observable user = {};
  @persist("object") @observable userFirebase = {};

  @observable dataDomisili = [];
  @observable dataPendidikanApoteker = [];

  @observable programs = {};
  @observable invoices = {};
  @observable invoiceData = {};

  @observable programLocal = {}

  @persist("object") @observable md = {};
  @persist("object") @observable md1 = {};

  @persist("object") @observable qmid = {};

  @persist @observable notificationToken = "";

  @observable serverTimeOffset = 0;
  hasListenUser = false;

  @persist @observable syncVersion = 1;
  @persist @observable lastSyncedProgramAt = 0;
  @persist("object") @observable syncPrograms = {};
  @observable syncProgramStore;

  @observable sessionStore;
  _isRegisteringSession = false;
  _sessionCheckEnabled = false;
  _sessionKickModalShown = false;

  constructor() {
    this.syncProgramStore = new SyncProgramStore(this)
    this.sessionStore = new SessionStore(this)
  }

  createBrowserSessionId() {
    return generateUUID();
  }

  clearBrowserSessionId() {
    try {
      localStorage.removeItem(BROWSER_SESSION_STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear browser session id", error);
    }
  }

  getBrowserSessionId() {
    try {
      let sessionId = localStorage.getItem(BROWSER_SESSION_STORAGE_KEY);
      if (!sessionId) {
        sessionId = this.createBrowserSessionId();
        localStorage.setItem(BROWSER_SESSION_STORAGE_KEY, sessionId);
      }
      return sessionId;
    } catch (error) {
      console.error("Failed to get browser session id", error);
      return null;
    }
  }

  @action sessionCheck() {
    if (!this.authenticated || this._sessionKickModalShown) return;
    const localId = this.getBrowserSessionId();
    if (!localId) return;
    return this.sessionStore.sessionCheck(localId);
  }

  @action handleSessionKickedOut() {
    if (this._sessionKickModalShown) {
      return Promise.resolve();
    }

    this._sessionKickModalShown = true;
    this._sessionCheckEnabled = false;

    if (typeof swal !== "undefined") {
      return swal({
        title: "Sesi di perangkat ini berakhir",
        text:
          "Akun kamu sedang aktif di browser atau perangkat lain, jadi sesi di sini kami tutup untuk keamanan. " +
          "Silakan masuk lagi kalau ingin melanjutkan dari perangkat ini.",
        icon: "warning",
        confirmButtonText: "Oke, mengerti",
        closeOnClickOutside: false,
        closeOnEsc: false,
      }).then(() => this.logout());
    }

    return this.logout();
  }

  @action logout() {
    if (this.userFirebase.uid) {
      MainDatabaseRef.child("users")
        .child(this.userFirebase.uid)
        .off();
    }

    return firebaseApp
      .auth()
      .signOut()
      .then(
        () => {
          this.resetData();
        },
        (error) => console.log(error)
      );
  }

  baseURL = "https://us-central1-obat-ukai.cloudfunctions.net/api";

  @action resetData() {
    this.authenticated = false;
    this.authenticating = false;

    this.notificationToken = "";

    this.user = {};
    this.userFirebase = {};

    this.programs = {};
    this.invoices = {};
    this.invoiceData = {};
    this.syncPrograms = {};
    this.lastSyncedProgramAt = 0;
    this.syncVersion = this.syncProgramStore.CurrentVersion;

    this.md = {};
    this._sessionCheckEnabled = false;
    this._sessionKickModalShown = false;
    this.clearBrowserSessionId();
  }

  @action getFirebaseIdToken() {
    // console.log('🔍 getFirebaseIdToken called');
    // console.log('🔍 authenticated:', this.authenticated);
    // console.log('🔍 userFirebase:', this.userFirebase);

    let firebaseUser = firebaseApp.auth().currentUser;

    if (firebaseUser) {
      console.log('🔍 Getting token from current user');
      return firebaseUser.getIdToken().then((token) => {
        console.log('🔍 Token received from current user:', token ? 'YES' : 'NO');
        return Promise.resolve(token);
      }).catch((error) => {
        console.error('❌ Error getting token from current user:', error);
        return Promise.reject(error);
      });
    } else {
      console.log('🔍 No current user, checking auth state');
      return new Promise((resolve, reject) => {
        const unsubscribe = firebaseApp.auth().onAuthStateChanged((user) => {
          unsubscribe(); // Unsubscribe after first call

          if (user) {
            console.log('🔍 User found in auth state, getting token');
            user.getIdToken().then((token) => {
              console.log('🔍 Token received from auth state:', token ? 'YES' : 'NO');
              resolve(token);
            }).catch((error) => {
              console.error('❌ Error getting token from auth state:', error);
              reject(error);
            });
          } else {
            console.log('❌ No user in auth state');
            this.resetData();
            reject(new Error('No user authenticated'));
          }
        });
      });
    }
  }

  @action setQMID(packid, qid, qdata) {
    let newData = Object.assign({}, this.qmid);
    if (!newData[packid]) {
      newData[packid] = {};
    }

    newData[packid][qid] = qdata;

    this.qmid = newData;
  }

  @action registerFirebase({ email, password, name, hp }) {
    this.authenticating = true;

    return firebaseApp
      .auth()
      .createUserWithEmailAndPassword(email, password)
      .then((userCredential) => {

        this.userFirebase = userCredential.user;
        this.authenticating = false;
        this.authenticated = true;

        if (name) {
          this.updateUser("name", name);
        }
        // if(hp){this.updateUser('hp', hp)}

        // this.registerOBATio({
        //   name: name,
        //   hp: hp,
        // })

        return this.loadAndListenUser();
      });
  }

  @action registerOBATio({ name, hp, verificationMethod }) {
    // console.log(this.getFirebaseIdToken())

    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          name: name,
          hp: hp,
          verificationMethod: verificationMethod,
          source: "ukai",
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/main_api/register"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          // console.log(isSuccess)
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve(responseJson);
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action verifyOBATioOTP({ code }) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          code: code,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/main_api/otp_check"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            // $('#confirmationModal').modal('hide');

            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action forgetPassword(email) {
    this.authenticating = true;

    return firebaseApp
      .auth()
      .sendPasswordResetEmail(email)
      .then(() => {
        this.authenticating = false;
        swal("Cek email kamu ya");
      })
      .catch((error) => {
        this.authenticating = false;
        swal("Ups..", "Gagal mengirimkan: " + error.message, "error");
      });
  }

  @action signInWithEmailAndPassword(email, password) {
    this.authenticating = true;

    return firebaseApp
      .auth()
      .signInWithEmailAndPassword(email, password)
      .then((userCredential) => {
        this.userFirebase = userCredential.user;
        this.authenticating = false;
        this.authenticated = true;
        return this.loadAndListenUser();
      });
  }

  @action userListenAndAuth(userCredential) {
    this.userFirebase = userCredential.user;
    this.authenticating = false;
    this.authenticated = true;
    return this.loadAndListenUser();
  }

  @action signInWithToken(token) {
    this.authenticating = true;

    // alert("we3");
    return firebaseApp
      .auth()
      .signInWithCustomToken(token)
      .then((userCredential) => {
        this.userFirebase = userCredential.user;
        this.authenticating = false;
        this.authenticated = true;
        return this.loadAndListenUser();
      });
    // .catch(function(error) {
    //   // Handle Errors here.
    //   var errorCode = error.code;
    //   var errorMessage = error.message;

    //   // this.userFirebase = userCredential.user;
    //   // this.authenticating = false;
    //   // this.authenticated = true;
    //   // ...
    //   console.log
    // });

    // return firebaseApp.auth().signInWithEmailAndPassword(email, password)
    // .then((userCredential) => {
    //   this.userFirebase = userCredential.user;
    //   this.authenticating = false;
    //   this.authenticated = true;
    //   return this.loadAndListenUser();
    // })
  }

  @action checkAuth() {
    this.authenticating = true;

    if (this._authUnsub) {
      this._authUnsub();
    }

    this._authUnsub = firebaseApp.auth().onAuthStateChanged((user) => {
      if (user) {
        this.userFirebase = user.toJSON();
        this.authenticated = true;
        this.authenticating = false;
        return this.loadAndListenUser();
      }

      this.authenticated = false;
      this.authenticating = false;
    });
  }

  @action loadAndListenUser() {
    // console.log("panggil")
    let promises = [this.loadUserData()];

    return Promise.all(promises);
  }

  @action loadUserData() {
    if (!this.userFirebase.uid) {
      return Promise.resolve();
    }

    return MainDatabaseRef.child("users")
      .child(this.userFirebase.uid)
      .once("value")
      .then(async (snapshot) => {
        let user = snapshot.val();


        this.user = user;
        const localId = this.getBrowserSessionId();
        if (localId && !isInIframe()) {
          const isRegistered = await this.sessionStore.sessionRegister({
            sessionId: localId,
            userAgent: navigator.userAgent,
          });
          if (isRegistered) {
            this._sessionCheckEnabled = true;
            await this.sessionStore.sessionCheck(localId);
          }
        }

        setTimeout(() => {
          this.listenUserData();
        }, 3000);
      });
  }

  @action listenUserData() {
    if (!this.userFirebase.uid) {
      return Promise.resolve();
    }

    MainDatabaseRef.child("users")
      .child(this.userFirebase.uid)
      .on("value", (snapshot) => {
        let data = snapshot.val();
        this.user = data;
        if (this._sessionCheckEnabled && !isInIframe()) {
          this.sessionCheck();
        }
      });

    // if(this.hasListenUser){
    //   return
    // }
    // this.hasListenUser =  true;

    // let promise1 = MainDatabaseRef.child('users').child(this.userFirebase.uid).on('child_added', (snapshot)=>{
    //   let data = snapshot.val();
    //   let key = snapshot.key;

    //   let newUser = Object.assign({}, this.user)
    //   newUser[key] = newUser

    //   if(newUser[key]){
    //     // console.log("udah ada")
    //   } else {
    //     this.user = newUser
    //   }
    // })

    // let promise2 = MainDatabaseRef.child('users').child(this.userFirebase.uid).on('child_changed', (snapshot)=>{
    //   let data = snapshot.val();
    //   let key = snapshot.key;
    //   // console.log(key)
    //   // console.log(data)
    //   let newUser = Object.assign({}, this.user)
    //   newUser[key] = newUser


    //   this.user = newUser
    // })

    // let promise3 = MainDatabaseRef.child('users').child(this.userFirebase.uid).on('child_removed', (snapshot)=>{
    //   let data = snapshot.val();
    //   let key = snapshot.key;

    //   let newUser = Object.assign({}, this.user)
    //   delete newUser[key]


    //   this.user = newUser
    // })

    // return Promise.all([promise2,promise3])
  }

  @action updateUser(property, value, noDelay = false) {
    return MainDatabaseRef.child("users")
      .child(this.userFirebase.uid)
      .child(property)
      .set(value);
  }

  @action updateConfirmation(invoiceKey, property, value) {
    return MainDatabaseRef.child("invoices")
      .child(invoiceKey)
      .child("confirmation")
      .child(property)
      .set(value);
  }

  @action handleRegisterNext() {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_api/registernext"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action handleChooseProgram(pid, voucher_code = "") {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          pid: pid,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_api/registerchooseprogram"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action handleCheckout(obj) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          source: "obatukai",
          referral_code: obj.referral_code,
          payment_method: obj.payment_method,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_api/registercheckout"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action handleConfirmPayment(invoiceKey) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          invoiceKey: invoiceKey,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_api/registerconfirmpayment"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action loadPrograms() {
    return UkaiDatabaseRef.child("programs")
      .orderByChild("isActive")
      .startAt(true)
      .endAt(true)
      .once("value")
      .then((snapshot) => {
        let programs = snapshot.val();

        this.programs = programs;


        return Promise.resolve(programs);
      });
  }

  @action loadProgramsStartedAt(startedAt) {
    return UkaiDatabaseRef.child("programs")
      .orderByChild("isActive")
      .startAt(startedAt)
      .once("value")
      .then((snapshot) => {
        let programs = snapshot.val();
        this.programs = programs;
        return Promise.resolve(programs);
      });
  }

  @action loadProgram(pid) {
    return UkaiDatabaseRef.child("programs")
      .child(pid)
      .once("value")
      .then((snapshot) => {
        return Promise.resolve(snapshot.val());
      });
  }

  @action loadInvoices() {
    return MainDatabaseRef.child("user-invoices")
      .child(this.userFirebase.uid)
      .once("value")
      .then((snapshot) => {
        let invoices = snapshot.val();

        this.invoices = invoices;


        return this.listenInvoiceData();
      });
  }

  @action listenInvoiceData() {
    let arrPromises = [];

    if (!this.invoices) {
      return;
    }

    for (var i = 0; i < Object.keys(this.invoices).length; i++) {
      let invoiceKey = Object.keys(this.invoices)[i];

      let loaderInvoice = MainDatabaseRef.child("invoices")
        .child(invoiceKey)
        .once("value")
        .then((snapshot) => {
          let invoice = snapshot.val();

          let newInvoice = Object.assign({}, this.invoiceData);
          newInvoice[invoiceKey] = invoice;

          this.invoiceData = newInvoice;
        });

      let listenerInvoice = MainDatabaseRef.child("invoices")
        .child(invoiceKey)
        .on("value", (snapshot) => {
          let invoice = snapshot.val();

          let newInvoice = Object.assign({}, this.invoiceData);
          newInvoice[invoiceKey] = invoice;

          this.invoiceData = newInvoice;
        });

      arrPromises.push(loaderInvoice);
      arrPromises.push(listenerInvoice);
    }

    return Promise.all(arrPromises);
  }

  @action handleConfirmUserForTreasurer(iid, type) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          iid: iid,
          type: type,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_api/Treasurer/confirminvoice"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action handleStartExam(packid) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          packid: packid,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_api/startexam"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action handleFinishExam(packid) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          packid: packid,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_api/finishexam"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action handleNgoreksi(packid) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          packid: packid,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_api/ngoreksi"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            // swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action handleResetExam(packid) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          packid: packid,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_api/resetexam"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action addMentorToProgramGroup(mentor_uid, pid, gid) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          mentor_uid: mentor_uid,
          pid: pid,
          gid: gid,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_apiSuperMentor/addmentor"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action removeMentorFromProgramGroup(mentor_uid, pid, gid) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          mentor_uid: mentor_uid,
          pid: pid,
          gid: gid,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_apiSuperMentor/removementor"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action addStudentToProgramGroup(student_uid, pid, gid) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          student_uid: student_uid,
          pid: pid,
          gid: gid,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_apiSuperMentor/addstudent"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action handleUnbannedStudent(student_uid) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          student_uid: student_uid,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_api/Treasurer/unbannedStudent"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action removeStudentFromProgramGroup(student_uid, pid, gid) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          student_uid: student_uid,
          pid: pid,
          gid: gid,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_apiSuperMentor/removestudent"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action removeGroupFromProgram(pid, gid) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          pid: pid,
          gid: gid,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_apiSuperMentor/removegroup"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action editProgramEvents(pid, events) {
    return this.editEventsGroupFromProgram(pid, "", events);
  }

  @action editEventsGroupFromProgram(pid, gid, events) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          pid: pid,
          gid: gid,
          events: events,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_apiSuperMentor/editEvents"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action removePackage(packid) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          packid: packid,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_apiSuperMentor/removepackage"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action optOutFreeTrial() {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_api/optoutfreetrial"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action chooseProgram() {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_api/chooseprogram"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action optInProgram(pid) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          pid: pid,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;

      return fetch(
        functionUrl("/ukai_api/optinprogram"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action handleAcceptAgreement() {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_api/acceptagreement"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action setServerTimeOffset(data) {
    this.serverTimeOffset = data || 0;
  }

  @action loadDataDomisili() {
    PublicDataDatabaseRef.child("data-domisili")
      .once("value")
      .then((snapshot) => {
        let dataDomisili = snapshot.val();
        this.dataDomisili = dataDomisili;
      });
  }

  @action loadPendidikanApoteker() {
    PublicDataDatabaseRef.child("data-pendidikan-apoteker")
      .once("value")
      .then((snapshot) => {
        let dataPendidikanApoteker = snapshot.val();
        this.dataPendidikanApoteker = dataPendidikanApoteker.sort();
      });
  }

  @action getVoucherData(voucher_code, pass) {
    let data = {
      method: "POST",
      credentials: "omit",
      mode: "cors",
      body: JSON.stringify({
        code: voucher_code,
        pass: pass,
      }),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    };

    let isSuccess = false;


    return fetch(
      functionUrl("/ukai_apiPublicUkai/voucher_checker"),
      data
    )
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          isSuccess = true;
        } else {
          isSuccess = false;
        }

        return response.text().then((responseText) => {
          try {
            const responseJson = JSON.parse(responseText);
            return responseJson;
          } catch (err) {
            isSuccess = false;
            // return responseText
            return Promise.reject({ message: responseText });
          }
        });
      })
      .then((responseJson) => {
        if (isSuccess) {
          // swal(responseJson.message)
          // swal("Sukses..", responseJson, "success");
          return Promise.resolve(responseJson);
        } else {
          // swal("Ups..",responseJson.message, "error")
          return Promise.reject(responseJson);
        }
      })
      .catch((e) => {
        swal("Ups..", e.message, "error");
        return Promise.reject();
      });
  }

  @action createVoucher({
    name,
    email,
    domisili,
    whatsapp,
    bank_account,
    bank_name_holder,
    referral_code,
  }) {
    let data = {
      method: "POST",
      credentials: "omit",
      mode: "cors",
      body: JSON.stringify({
        name: name,
        email: email,
        domisili: domisili,
        whatsapp: whatsapp,
        bank_account: bank_account,
        bank_name_holder: bank_name_holder,
        referral_code: referral_code,
      }),
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    };

    let isSuccess = false;


    return fetch(
      functionUrl("/ukai_apiPublicUkai/create_voucher"),
      data
    )
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          isSuccess = true;
        } else {
          isSuccess = false;
        }

        return response.text().then((responseText) => {
          try {
            const responseJson = JSON.parse(responseText);
            return responseJson;
          } catch (err) {
            isSuccess = false;
            // return responseText
            return Promise.reject({ message: responseText });
          }
        });
      })
      .then((responseJson) => {
        if (isSuccess) {
          // swal(responseJson.message)
          // swal("Sukses..", responseJson, "success");
          return Promise.resolve(responseJson);
        } else {
          // swal("Ups..",responseJson.message, "error")
          return Promise.reject(responseJson);
        }
      })
      .catch((e) => {
        swal("Ups..", e.message, "error");
        return Promise.reject();
      });
  }

  @action checkoutMidtrans(iid) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          iid: iid,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_api/checkout_midtrans"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              isSuccess = true;
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            return Promise.resolve(responseJson);
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action handleLoadVideoApi(video_code) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          video_code: video_code,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;


      return fetch(
        functionUrl("/ukai_api/Video/getVideoOTP"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              isSuccess = true;
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            return Promise.resolve(responseJson);
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }

  @action async askForPermissioToReceiveNotifications() {
    try {
      const messaging = firebase.messaging();
      await messaging.requestPermission();
      const token = await messaging.getToken();

      firebase
        .database()
        .ref("user-token")
        .child(this.userFirebase.uid)
        .child(token)
        .set(true);

      this.notificationToken = token;

      return token;
    } catch (error) {
      if (error.code === "messaging/permission-blocked") {
        // swal("Ups..","Notifikasi diblokir oleh browser kamu.", "error");
      }
      this.notificationToken = "";
      console.error(error);
    }
  }

  @action updateExamSearch(packid) {
    return this.getFirebaseIdToken().then((token) => {
      let data = {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        body: JSON.stringify({
          packid: packid,
        }),
        headers: {
          Authorization: "Bearer " + token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      };

      let isSuccess = false;

      return fetch(
        functionUrl("/ukai_apiSuperMentor/examUpdateSearch"),
        data
      )
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            isSuccess = true;
          } else {
            isSuccess = false;
          }

          return response.text().then((responseText) => {
            try {
              const responseJson = JSON.parse(responseText);
              return responseJson;
            } catch (err) {
              isSuccess = false;
              // return responseText
              return Promise.reject({ message: responseText });
            }
          });
        })
        .then((responseJson) => {
          if (isSuccess) {
            // swal(responseJson.message)
            swal("Sukses..", responseJson.message, "success");
            return Promise.resolve();
          } else {
            // swal("Ups..",responseJson.message, "error")
            return Promise.reject(responseJson);
          }
        })
        .catch((e) => {
          swal("Ups..", e.message, "error");
          return Promise.reject();
        });
    });
  }
}

export default AppState;
